import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Text, Flex, useToast, Skeleton, Switch, Icon } from '@chakra-ui/react';
import FeatherIcon from '@ailibs/feather-react-ts';
import { PiTrophy } from 'react-icons/pi';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import { ShortcutCard } from '../Home/components/ShortcutCard';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { ApplicationComponentId } from '@/model/application-component';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import { ReactComponent as GuessIcon } from '@/assets/icons/guess.svg';

type Link = {
  label: string;
  url: string;
  icon: ReactNode;
};

export const ApplicationChampionships = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isAppAreaActive, setIsAppAreaActive] = useState(false);

  const links: Array<Link> = [
    {
      // Competições
      label: t('pages.application_championship.application_championship_link_competitions'),
      url: '/application/championships/championship',
      icon: <PiTrophy />,
    },
    {
      // Temporadas
      label: t('pages.application_championship.application_championship_link_seasons'),
      url: '/application/championships/seasons',
      icon: <FeatherIcon name='calendar' />,
    },
    {
      // Times
      label: t('pages.application_championship.application_championship_link_teams'),
      url: '/application/championships/teams',
      icon: <FeatherIcon name='shield' />,
    },
    {
      // Tabelas de Jogos (ícone maior)
      label: t('pages.application_championship.application_championship_link_game_tables'),
      url: '/application/championships/matches',
      icon: <Icon as={GuessIcon} color='brand.500' boxSize='32px' />,
    },
    {
      // Tabelas de Classificação
      label: t('pages.application_championship.application_championship_link_standings_tables'),
      url: '/application/championships/standings',
      icon: <FeatherIcon name='list' />,
    },
  ];

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(
    undefined,
    ApplicationComponentId.TABLES,
  );
  const { mutate: enableAppAreaMutation, isPending: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successKey = pendingAppAreaState ? 'toggled_enable' : 'toggled_disable';
    const errorKey = pendingAppAreaState ? 'toggle_failed_enable' : 'toggle_failed_disable';

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation(
      [
        { component_id: ApplicationComponentId.TABLE_CLASSIFICATION, active: pendingAppAreaState },
        { component_id: ApplicationComponentId.GAME_SCHEDULE, active: pendingAppAreaState },
      ],
      {
        onSuccess: () => {
          toast({
            status: 'success',
            title: t(`pages.application_championship.application_championship_${successKey}`),
          });
        },
        onError: () => {
          toast({
            status: 'error',
            title: t(`pages.application_championship.application_championship_${errorKey}`),
          });

          setIsAppAreaActive(!pendingAppAreaState);
        },
      },
    );

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent title={t('pages.application_championship.application_championship_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex alignItems='center' gap={4}>
          {isLoadingAppArea ? (
            <>
              <Skeleton w='130px' h='21px' />
              <Skeleton w='102px' h='21px' />
            </>
          ) : (
            <>
              <Text fontWeight='bold' color='brand.500'>
                {t('pages.application_championship.application_championship_title')}
              </Text>

              <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                {isAppAreaActive
                  ? t('pages.application_championship.application_championship_enabled')
                  : t('pages.application_championship.application_championship_disabled')}
              </Switch>
            </>
          )}
        </Flex>

        <Flex gap='xxsmall' flexWrap='wrap'>
          {links?.map(link => (
            <Link key={link.url} to={link.url}>
              <ShortcutCard label={link.label} icon={link.icon} />
            </Link>
          ))}
        </Flex>
      </SectionBox>

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_calendar.calendar_title_text')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_championship.application_championship_description_enabled')
              : t('pages.application_championship.application_championship_description_disabled')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={isLoadingEnableOrDisableAppArea} />
    </PageContent>
  );
};
